import * as React from 'react';
import { cn } from '@/lib/utils';

interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 0–100. Values outside the range are clamped. */
  value: number;
  /** Track + indicator colour token. Defaults to primary. */
  tone?: 'primary' | 'success' | 'warning' | 'danger' | 'driver';
  /** Optional CSS variable colour (overrides tone). Used for per-driver tints. */
  indicatorColor?: string;
}

// Lightweight progress track. We don't need Radix's full Progress primitive
// here — it adds ARIA live updates we don't need for static rendering.
// For accessible use cases, consumers can pass role="progressbar" + aria-*.
export function Progress({
  value,
  tone = 'primary',
  indicatorColor,
  className,
  ...props
}: ProgressProps) {
  const clamped = Math.min(100, Math.max(0, value));
  const toneClass = {
    primary: 'bg-primary',
    success: 'bg-emerald-500',
    warning: 'bg-amber-500',
    danger: 'bg-rose-500',
    driver: '',
  }[tone];
  return (
    <div
      className={cn(
        'h-1.5 w-full overflow-hidden rounded-full bg-muted',
        className,
      )}
      {...props}
    >
      <div
        className={cn('h-full rounded-full transition-all duration-standard ease-standard', toneClass)}
        style={{
          width: `${clamped}%`,
          backgroundColor: indicatorColor,
        }}
      />
    </div>
  );
}
